import React from 'react';
import './CardDetailModal.css';
import { seedBadgeText, seedProgressLabel } from '../utils/seedTier';

// Reuse constants from CardHand
const CARD_TYPE_STYLE = {
  attack:  { icon: '⚔️', color: '#e74c3c', label: 'Attack' },
  spell:   { icon: '🔮', color: '#9b59b6', label: 'Spell' },
  block:   { icon: '🛡️', color: '#3498db', label: 'Block' },
  skill:   { icon: '🌳', color: '#27ae60', label: 'Skill' },
  power:   { icon: '⚡', color: '#f39c12', label: 'Power' },
  special: { icon: '✨', color: '#1abc9c', label: 'Special' },
  heal:    { icon: '💚', color: '#10b981', label: 'Heal' },
  utility: { icon: '🔧', color: '#9ca3af', label: 'Utility' },
};

const ELEMENT_STYLE = {
  Fire:        { icon: '🔥', color: '#e74c3c' },
  Cold:        { icon: '❄️', color: '#3498db' },
  Ice:         { icon: '❄️', color: '#3498db' },
  Electricity: { icon: '⚡', color: '#f1c40f' },
  Lightning:   { icon: '⚡', color: '#f1c40f' },
  Acid:        { icon: '🧪', color: '#27ae60' },
  Water:       { icon: '💧', color: '#2980b9' },
  Earth:       { icon: '🪨', color: '#8B7355' },
  Air:         { icon: '🌪️', color: '#bdc3c7' },
  Sonic:       { icon: '🔊', color: '#9b59b6' },
  Dark:        { icon: '🌑', color: '#6c3483' },
  Light:       { icon: '☀️', color: '#f9e79f' },
  Holy:        { icon: '✝️', color: '#f9e79f' },
  Psionic:     { icon: '🧠', color: '#a569bd' },
  Arcane:      { icon: '💫', color: '#8e44ad' },
  Healing:     { icon: '💚', color: '#27ae60' },
};

const KEYWORD_DESCRIPTIONS = {
  Exhaust:    'Removed from your deck after playing. Cannot be reshuffled.',
  Retain:     'Stays in your hand between turns instead of being discarded.',
  Innate:     'Always drawn in your opening hand.',
  Ethereal:   'If still in your hand at end of turn, it is exhausted (removed).',
  Unplayable: 'Cannot be played directly — triggers automatically or via other effects.',
};

const KEYWORD_STYLE = {
  Exhaust:    { color: '#e74c3c', bg: 'rgba(231,76,60,0.2)' },
  Retain:     { color: '#f39c12', bg: 'rgba(243,156,18,0.2)' },
  Innate:     { color: '#2ecc71', bg: 'rgba(46,204,113,0.2)' },
  Ethereal:   { color: '#9b59b6', bg: 'rgba(155,89,182,0.2)' },
  Unplayable: { color: '#e74c3c', bg: 'rgba(231,76,60,0.15)' },
};

const SOURCE_LABELS = {
  class:          '🗡️ Class Card',
  equipment:      '🛡️ Equipment',
  seed:           '🌿 Magic Seed',
  skill_tree:     '🌳 Skill Tree',
  advanced_class: '⭐ Advanced Class',
  race:           '👤 Racial',
  alignment:      '⚖️ Alignment',
  level_up:       '📈 Level Up',
  set_bonus:      '🏷️ Set Bonus',
  junk:           '🪨 Junk',
};

const RARITY_LABELS = {
  common:    { label: 'Common',    color: '#aaa' },
  uncommon:  { label: 'Uncommon',  color: '#2ecc71' },
  rare:      { label: 'Rare',      color: '#3498db' },
  epic:      { label: 'Epic',      color: '#9b59b6' },
  legendary: { label: 'Legendary', color: '#f39c12' },
  junk:      { label: 'Junk',      color: '#9ca3af' },
};

/**
 * Full-screen card detail modal.
 * Shows: name, type, cost, element, description, keywords with explanations,
 * source, rarity, effects breakdown.
 *
 * When `onPlay` is supplied it doubles as the enlarged/selected view: clicking the
 * card frame (or the Play button) plays the card; clicking outside cancels.
 */
const CardDetailModal = ({ card, onClose, onPlay, canPlay, playDisabledReason, isPending }) => {
  if (!card) return null;

  const playMode = typeof onPlay === 'function';
  const handleFrameClick = (e) => {
    e.stopPropagation();
    if (playMode && canPlay) onPlay();
  };
  const typeStyle = CARD_TYPE_STYLE[card.type] || CARD_TYPE_STYLE.special;
  const rarityInfo = RARITY_LABELS[card.rarity] || RARITY_LABELS.common;
  const element = card._spellMeta?.element || card.effects?.magicDamage?.element;
  const elemStyle = element ? ELEMENT_STYLE[element] : null;
  const isLegendary = card.rarity === 'legendary';
  const isJunk = card.source === 'junk' || card.isJunk || card.rarity === 'junk';
  const isSetBonus = card.source === 'set_bonus' || card.effects?._setBonus;
  const seedLevel = card._seedLevel || 0;
  const showSeed = card.source === 'seed' && seedLevel > 0;

  // Build effect details
  const effectDetails = [];
  const eff = card.effects || {};

  if (eff.action === 'attack') {
    effectDetails.push({ icon: '⚔️', text: 'Performs a melee attack' });
    if (eff.damageMultiplier && eff.damageMultiplier !== 1) {
      effectDetails.push({ icon: '💥', text: `${Math.round(eff.damageMultiplier * 100)}% damage modifier` });
    }
    if (eff.bonusDamage) effectDetails.push({ icon: '➕', text: `+${eff.bonusDamage} bonus damage` });
    if (eff.multiHit) effectDetails.push({ icon: '🔄', text: `Hits ${eff.multiHit} times` });
    if (eff.selfDamagePercent) effectDetails.push({ icon: '💢', text: `Take ${eff.selfDamagePercent}% HP as recoil` });
  }
  if (eff.action === 'spell') {
    effectDetails.push({ icon: '🔮', text: `Casts ${eff.spell}` });
  }
  if (eff.aoeType === 'cleave' && eff.splashCount) {
    const pct = Math.round((eff.splashPercent || 0) * 100);
    const targets = eff.splashCount >= 999
      ? 'every other enemy'
      : `up to ${eff.splashCount} additional ${eff.splashCount === 1 ? 'enemy' : 'enemies'}`;
    effectDetails.push({ icon: '💫', text: `Also hits ${targets} for ${pct}% damage` });
    effectDetails.push({ icon: 'ℹ️', text: 'Needs more than one enemy — no extra value one-on-one' });
  }
  if (eff.dotSpread) {
    effectDetails.push({ icon: '☠️', text: 'Spreads its damage-over-time to every other enemy' });
    effectDetails.push({ icon: 'ℹ️', text: 'Needs more than one enemy — no extra value one-on-one' });
  }
  if (eff.action === 'ability') {
    effectDetails.push({ icon: '⚡', text: `Uses ${eff.ability}` });
  }
  if (eff.magicDamage) {
    const md = eff.magicDamage;
    let dmgText = `${md.base || 0} base`;
    if (md.multiplier) dmgText += ` + ${Math.round(md.multiplier * 100)}% ${md.stat || 'MEN'}`;
    if (md.perLevel) dmgText += ` + ${md.perLevel}/level`;
    effectDetails.push({ icon: '✨', text: `Magic damage: ${dmgText}` });
    if (md.element) effectDetails.push({ icon: (ELEMENT_STYLE[md.element]?.icon || '🔮'), text: `${md.element} elemental` });
  }
  if (eff.lifesteal) {
    effectDetails.push({ icon: '🩸', text: `Lifesteal: heals ${Math.round(eff.lifesteal * 100)}% of damage dealt` });
  }
  if (eff.blockStat || eff.blockFlat) {
    let blockText = '';
    if (eff.blockFlat) blockText = `${eff.blockFlat} flat`;
    if (eff.blockStat) blockText += `${blockText ? ' + ' : ''}${Math.round((eff.blockMultiplier || 1) * 100)}% ${eff.blockStat}`;
    effectDetails.push({ icon: '🛡️', text: `Block: ${blockText}` });
  }
  if (eff.heal) {
    let healText = '';
    if (typeof eff.heal === 'number') {
      healText = `${eff.heal} HP`;
    } else {
      healText = `${eff.heal.flat || 0}`;
      if (eff.heal.stat) healText += ` + ${Math.round((eff.heal.multiplier || 0.3) * 100)}% ${eff.heal.stat}`;
    }
    effectDetails.push({ icon: '💚', text: `Heal: ${healText}` });
  }
  if (eff.healPercent) {
    effectDetails.push({ icon: '💚', text: `Heals ${eff.healPercent}% of max HP` });
  }
  if (eff.draw) effectDetails.push({ icon: '📤', text: `Draw ${eff.draw} card${eff.draw > 1 ? 's' : ''}` });
  if (eff.apGain) effectDetails.push({ icon: '⚡', text: `Gain ${eff.apGain} AP` });
  if (eff.scry) effectDetails.push({ icon: '🔮', text: `Scry ${eff.scry} — peek and reorder top cards` });
  if (eff.buff) effectDetails.push({ icon: '✨', text: `Buff: +${eff.buff.value} ${eff.buff.type} for ${eff.buff.rounds} rounds` });
  if (eff.drBuff) effectDetails.push({ icon: '🛡️', text: `+${eff.drBuff.value} Damage Reduction for ${eff.drBuff.rounds} round` });
  if (eff.debuff) effectDetails.push({ icon: '💜', text: `Debuff: -${eff.debuff.value} ${eff.debuff.type} for ${eff.debuff.rounds} rounds` });
  if (eff.hidden) effectDetails.push({ icon: '🌑', text: 'Hide in shadows (Thief)' });
  if (eff.thirdEye) effectDetails.push({ icon: '👁️', text: 'Double all damage (Psion)' });
  if (eff.powerCast) effectDetails.push({ icon: '🔮', text: `Next spell deals +${Math.round(eff.powerCast * 100)}% damage` });
  if (eff.petCommand) effectDetails.push({ icon: '🐾', text: `Pet action: ${eff.petCommand}` });
  if (eff.discardHand) effectDetails.push({ icon: '🌪️', text: 'Discard entire hand' });
  if (eff.chaosSurge) effectDetails.push({ icon: '🎲', text: 'Random chaotic effect' });

  return (
    <div className="card-detail-overlay" onClick={onClose}>
      <div className="card-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="card-detail-close" onClick={onClose}>✕</button>

        {/* Card frame */}
        <div
          className={`card-detail-frame ${isLegendary ? 'legendary' : ''} ${isJunk ? 'junk' : ''} ${isSetBonus ? 'set-bonus' : ''} ${playMode && canPlay ? 'playable' : ''}`}
          style={{ borderColor: isLegendary ? '#f39c12' : (isJunk ? '#7f8c8d' : (isSetBonus ? '#d4af37' : typeStyle.color)) }}
          onClick={handleFrameClick}
        >
          {/* Legendary shimmer */}
          {isLegendary && <div className="card-detail-legendary-shimmer" />}

          {/* AP cost */}
          <div className="card-detail-ap" style={{ background: typeStyle.color }}>
            {card.apCost}
          </div>

          {/* Header */}
          <div className="card-detail-header">
            <span className="card-detail-type-icon">{typeStyle.icon}</span>
            <h3 className="card-detail-name">{card.name}</h3>
            {showSeed && (
              <span className="card-detail-seed-stars" title={seedProgressLabel(seedLevel)}>
                {seedBadgeText(seedLevel)}
              </span>
            )}
          </div>

          {/* Meta line: type + rarity + element */}
          <div className="card-detail-meta">
            <span className="card-detail-type-label" style={{ color: typeStyle.color }}>{typeStyle.label}</span>
            <span className="card-detail-rarity" style={{ color: rarityInfo.color }}>• {rarityInfo.label}</span>
            {elemStyle && (
              <span className="card-detail-element" style={{ color: elemStyle.color }}>
                • {elemStyle.icon} {element}
              </span>
            )}
            {card._spellMeta?.mp && (
              <span className="card-detail-mp">• {card._spellMeta.mp} MP</span>
            )}
          </div>

          {/* Divider */}
          <div className="card-detail-divider" style={{ borderColor: typeStyle.color }} />

          {/* Description */}
          <div className="card-detail-description">
            {card.description}
          </div>

          {/* Effect breakdown */}
          {effectDetails.length > 0 && (
            <div className="card-detail-effects">
              <div className="card-detail-section-title">Effects</div>
              {effectDetails.map((ed, i) => (
                <div key={i} className="card-detail-effect-row">
                  <span className="effect-icon">{ed.icon}</span>
                  <span className="effect-text">{ed.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Keywords with explanations */}
          {card.keywords && card.keywords.length > 0 && (
            <div className="card-detail-keywords-section">
              <div className="card-detail-section-title">Keywords</div>
              {card.keywords.map((kw, i) => {
                const kwStyle = KEYWORD_STYLE[kw] || { color: '#aaa', bg: 'rgba(150,150,150,0.2)' };
                return (
                  <div key={i} className="card-detail-kw-row">
                    <span className="card-detail-kw-badge" style={{ color: kwStyle.color, background: kwStyle.bg }}>
                      {kw}
                    </span>
                    <span className="card-detail-kw-desc">
                      {KEYWORD_DESCRIPTIONS[kw] || 'Special keyword.'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Seed upgrade info */}
          {showSeed && (
            <div className="card-detail-seed-info">
              🌿 {seedProgressLabel(seedLevel)}
            </div>
          )}

          {/* Source badge */}
          <div className="card-detail-source">
            {SOURCE_LABELS[card.source] || card.source}
          </div>
          {isSetBonus && card._setPieces && (
            <div className="card-detail-set">Set Tier: {card._setPieces} pieces</div>
          )}
        </div>

        {playMode ? (
          <div className="card-detail-play-bar">
            <button
              className="card-detail-play-btn"
              onClick={(e) => { e.stopPropagation(); if (canPlay) onPlay(); }}
              disabled={!canPlay}
            >
              {isPending ? 'Casting…' : canPlay ? `▶ Play ${card.name}` : (playDisabledReason || 'Cannot play')}
            </button>
            <div className="card-detail-hint">Tap the card to play · Tap outside to cancel</div>
          </div>
        ) : (
          <div className="card-detail-hint">Right-click a card to inspect · Click anywhere to close</div>
        )}
      </div>
    </div>
  );
};

export default CardDetailModal;
